import type { EditorContext } from "../../tools/EditorContext";
import type { Tool } from "../../tools/ToolBase";
import type { ToolId } from "../PropertiesPanel";
import type {
  ConstructionEditAction,
  ConstructionSubMode,
} from "../../tools/ConstructionTool";
import { ImageUploadDialog } from "../ImageUploadDialog";

/**
 * Dependencies threaded from the `app.ts` bootstrap into the construction
 * sub-toolbar wiring. This block was lifted verbatim out of the flat bootstrap
 * script (issue #880 / epic #828), so every field here is a local, closure, or
 * queried element the original inline block read from its enclosing scope.
 *
 * `getActiveTool` is a getter (not a bare `Tool | null`) because the original
 * block read the mutable `activeTool` binding inside a deferred callback (the
 * image-upload "placed" handler); passing the value would freeze it at
 * setup time. The four created elements the block assigned to outer `let`s
 * (edit-action divider + drag-point / smooth-toggle / slice buttons) are
 * RETURNED so `app.ts` can hand them to its sibling sync closures.
 */
export interface SetupConstructionSubbarDeps {
  constructionSubbar: HTMLElement | null;
  SUBTOOL_ORDER: ConstructionSubMode[];
  subtoolButtons: Map<ConstructionSubMode, HTMLButtonElement>;
  setConstructionSubMode: (id: ConstructionSubMode) => void;
  setEditActionAndSync: (action: ConstructionEditAction) => void;
  ctx: EditorContext;
  tools: Record<ToolId, Tool>;
  activateTool: (id: ToolId) => void;
  getActiveTool: () => Tool | null;
}

export interface ConstructionSubbarElements {
  editActionDivider: HTMLDivElement | null;
  dragPointBtn: HTMLButtonElement | null;
  smoothToggleBtn: HTMLButtonElement | null;
  addPointBtn: HTMLButtonElement | null;
  deletePointBtn: HTMLButtonElement | null;
  sliceBtn: HTMLButtonElement | null;
}

export function setupConstructionSubbar(
  deps: SetupConstructionSubbarDeps,
): ConstructionSubbarElements {
  const {
    constructionSubbar,
    SUBTOOL_ORDER,
    subtoolButtons,
    setConstructionSubMode,
    setEditActionAndSync,
    ctx,
    tools,
    activateTool,
    getActiveTool,
  } = deps;
  let editActionDivider: HTMLDivElement | null = null;
  let dragPointBtn: HTMLButtonElement | null = null;
  let smoothToggleBtn: HTMLButtonElement | null = null;
  let addPointBtn: HTMLButtonElement | null = null;
  let deletePointBtn: HTMLButtonElement | null = null;
  let sliceBtn: HTMLButtonElement | null = null;
  if (constructionSubbar) {
    // The buttons are static markup in index.html (#1230), so the page shows
    // the default sub-tool before this runs and with JavaScript off; this
    // wires them. The static ones start disabled: sub-tools get their state
    // from `syncToolAvailability`, the rest are enabled here.
    const adopt = (selector: string): HTMLButtonElement => {
      const el = constructionSubbar.querySelector<HTMLButtonElement>(selector);
      if (!el) throw new Error(`construction sub-toolbar: no ${selector}`);
      return el;
    };
    for (const id of SUBTOOL_ORDER) {
      const btn = adopt(`[data-subtool-id="${id}"]`);
      btn.addEventListener("click", () => setConstructionSubMode(id));
      subtoolButtons.set(id, btn);
    }
    // Sub-sub-tools live under Edit, below their divider (hidden until Edit
    // is active, see `syncConstructionSubbar`).
    editActionDivider =
      constructionSubbar.querySelector<HTMLDivElement>(".subtool-divider");
    const action = (id: string, run: () => void): HTMLButtonElement => {
      const btn = adopt(`[data-subtool-id="${id}"]`);
      btn.disabled = false;
      btn.addEventListener("click", run);
      return btn;
    };
    // Drag-point and smooth-toggle form a two-state radio pair (the explicit
    // indicator for Edit's default drag mode); add / delete point (#778,
    // #969) and the knife (#193) follow.
    dragPointBtn = action("drag-point", () => setEditActionAndSync("drag"));
    smoothToggleBtn = action("smooth-toggle", () =>
      setEditActionAndSync("smooth-toggle"),
    );
    addPointBtn = action("add-point", () => setEditActionAndSync("add-point"));
    deletePointBtn = action("delete-point", () =>
      setEditActionAndSync("delete"),
    );
    sliceBtn = action("slice", () => setEditActionAndSync("slice"));

    // Image subtool (issue #484, parent #75) — a one-shot action rather
    // than a persistent sub-mode: clicking opens the trace-an-image
    // upload dialog and leaves the active sub-mode untouched, so there's
    // no entry in ConstructionSubMode / SUBTOOL_ORDER. Never line-gated — a
    // photo is usually the FIRST thing in an empty project.
    const imageBtn = adopt("#construction-image-btn");
    imageBtn.disabled = false;
    const imageUploadDialog = new ImageUploadDialog(
      document.getElementById("modal-host")!,
      ctx,
      // After an image is placed, drop into the Construction Select
      // sub-mode so it can be moved immediately (issue #511).
      () => {
        if (getActiveTool() !== tools.construction)
          activateTool("construction");
        setConstructionSubMode("select");
      },
    );
    imageBtn.addEventListener("click", () => imageUploadDialog.open());
    // An upload that landed but was never added to the canvas survives an
    // accidental reload via a localStorage record (#543) — bring the
    // dialog back with the uploaded photo instead of stranding the file.
    void imageUploadDialog.restorePendingUpload();
  }
  return {
    editActionDivider,
    dragPointBtn,
    smoothToggleBtn,
    addPointBtn,
    deletePointBtn,
    sliceBtn,
  };
}
